import React, { useMemo } from 'react';
import type { DashboardModel } from '../../hooks/useDashboard';
import { getRunwayWindAdvisory } from '../../hooks/dashboard/dashboardUtils';

function classifyAircraft(name: string): 'turboprop' | 'jet' {
  const n = name.toLowerCase();
  if (/cessna|caravan|208|grand.?caravan|king.?air|dash.?8|dhc|twin.?otter|pilatus|pc.?12|atr|turboprop|piston|c208|grand|skymaster|310|402/.test(n)) {
    return 'turboprop';
  }
  return 'jet';
}

const DashboardMapOverlay: React.FC<DashboardModel> = ({
  dark,
  liveAircrafts,
  selectedAircraft,
  historyReplayData,
  replayIndex,
  replaySpeed,
  mapAirstrips,
  hoveredAirstrip,
  projectedPositions,
  projectedAirstrips,
  projectedReplayEndpoints,
  projectedTrailArrows,
  projectedReplayPos,
  mapRef,
  mapUpdateTrigger,
  isReplaying,
  isValidCoordinate,
  airstripWeatherByCode,
  setSelectedAircraft,
  setHoveredAirstrip,
}) => {
  const airstripLayerModel = useMemo(() => {
    const zoom = mapRef.current?.getZoom() ?? 0;
    const grid = 72;

    if (zoom < 7) {
      const clusters = new Map<string, { key: string; x: number; y: number; count: number; codes: string[] }>();
      for (const a of mapAirstrips) {
        const pos = projectedAirstrips[a.code];
        if (!pos) continue;
        const gx = Math.floor(pos.x / grid);
        const gy = Math.floor(pos.y / grid);
        const key = `${gx}:${gy}`;
        const existing = clusters.get(key);
        if (existing) {
          existing.count += 1;
          existing.codes.push(a.code);
          existing.x = (existing.x * (existing.count - 1) + pos.x) / existing.count;
          existing.y = (existing.y * (existing.count - 1) + pos.y) / existing.count;
        } else {
          clusters.set(key, { key, x: pos.x, y: pos.y, count: 1, codes: [a.code] });
        }
      }
      return {
        zoom,
        mode: 'cluster' as const,
        clusters: Array.from(clusters.values()),
        markers: [],
      };
    }

    return {
      zoom,
      mode: 'marker' as const,
      clusters: [],
      markers: mapAirstrips
        .map((a) => {
          const pos = projectedAirstrips[a.code];
          if (!pos) return null;
          return {
            ...a,
            x: pos.x,
            y: pos.y,
            weather: airstripWeatherByCode[a.code] || airstripWeatherByCode[a.icao || ''],
            windAdvisory: getRunwayWindAdvisory(
              a.runway,
              (airstripWeatherByCode[a.code] || airstripWeatherByCode[a.icao || ''])?.windDirectionDeg ?? null,
              (airstripWeatherByCode[a.code] || airstripWeatherByCode[a.icao || ''])?.windSpeedKt ?? null
            ),
          };
        })
        .filter(Boolean) as Array<(typeof mapAirstrips[number] & { x: number; y: number; weather?: DashboardModel['airstripWeatherByCode'][string]; windAdvisory: ReturnType<typeof getRunwayWindAdvisory> })>,
    };
  }, [mapRef, mapUpdateTrigger, mapAirstrips, projectedAirstrips, airstripWeatherByCode]);

  return (
    <div className="absolute inset-0 z-10 pointer-events-none">
      {liveAircrafts.filter(ac => ac.telemetry && isValidCoordinate(ac.telemetry.lat, ac.telemetry.lng)).map(ac => {
        const pos = projectedPositions[ac.id];
        if (!pos) return null;

        const speed = ac.telemetry?.groundSpeed || 0;
        const heading = ac.telemetry?.heading || 0;
        const isFlying = speed > 30;
        const isSelected = selectedAircraft?.id === ac.id;
        const shortTail = ac.tailNumber.replace('5H-', '');
        const acCategory = classifyAircraft(ac.name);

        return (
          <div
            key={ac.id}
            className="absolute top-0 left-0 pointer-events-auto"
            style={{
              transform: `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -50%)`,
              willChange: 'transform',
            }}
          >
            <div className="relative">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedAircraft(ac);
                  if (ac.telemetry && mapRef.current && isValidCoordinate(ac.telemetry.lat, ac.telemetry.lng)) {
                    try {
                      mapRef.current.flyTo({
                        center: [ac.telemetry.lng, ac.telemetry.lat],
                        zoom: 12,
                        duration: 1200,
                      });
                    } catch {}
                  }
                }}
                className="cursor-pointer relative flex items-center justify-center"
              >
                {/* Glow ring on selected */}
                {isSelected && (
                  <div className="absolute inset-0 rounded-full animate-ping bg-blue-500/30 scale-150" />
                )}
                <svg
                  className={`relative w-7 h-7 drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)] transition-transform ${
                    isSelected ? 'text-blue-400 scale-110' : isFlying ? 'text-white' : 'text-blue-300'
                  }`}
                  style={{ transform: `rotate(${heading}deg) ${isSelected ? 'scale(1.1)' : ''}` }}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  {acCategory === 'turboprop' ? (
                    <>
                      {/* Straight-wing turboprop body */}
                      <path d="M12 4.5L10.5 5.5L10.5 8.5L1 11.5L1 13.5L10.5 11L10.5 18.5L8 20L8 22L12 21L16 22L16 20L13.5 18.5L13.5 11L23 13.5L23 11.5L13.5 8.5L13.5 5.5Z"/>
                      {/* Prop disc at nose */}
                      <ellipse cx="12" cy="3" rx="3.5" ry="0.9"/>
                    </>
                  ) : (
                    /* Swept-wing jet */
                    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-4.5l8 2.5z"/>
                  )}
                </svg>
              </div>
              {/* Pill label */}
              <div className="absolute top-[32px] left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none">
                <div
                  className={`px-1.5 py-[2px] rounded-[5px] text-[9px] font-black tracking-widest
                    shadow-[0_2px_10px_rgba(0,0,0,0.65)] border ${
                    isSelected
                      ? 'bg-blue-600 border-blue-400/50 text-white'
                      : 'bg-[rgba(8,12,24,0.80)] border-white/15 text-white'
                  }`}
                >
                  {shortTail}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {projectedTrailArrows.map((arrow: { x: number; y: number; angle: number }, i: number) => (
        <div key={`trail-arrow-${i}`}
          className="absolute top-0 left-0 pointer-events-none"
          style={{ transform: `translate3d(${arrow.x}px,${arrow.y}px,0) translate(-50%,-50%) rotate(${arrow.angle + 90}deg)`, zIndex: 24, width: 0, height: 0,
            borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: '10px solid rgba(255,255,255,0.78)',
            filter: 'drop-shadow(0 0 2px rgba(245,158,11,0.7)) drop-shadow(0 0 1px rgba(0,0,0,0.7))' }}
        />
      ))}

      {projectedReplayEndpoints && (() => {
        const first = historyReplayData[0];
        const last = historyReplayData[historyReplayData.length - 1];
        const mkLabel = (t: string) => {
          try { return new Date(t).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
        };
        return (
          <>
            <div className="absolute top-0 left-0 pointer-events-none"
              style={{ transform: `translate3d(${projectedReplayEndpoints.start.x}px, ${projectedReplayEndpoints.start.y}px, 0) translate(-50%, -50%)`, zIndex: 25 }}>
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <div style={{ width: 13, height: 13, borderRadius: '50%', background: '#22c55e', border: '2.5px solid white', boxShadow: '0 0 10px rgba(34,197,94,0.85)' }}/>
                <div style={{ background: 'rgba(8,12,24,0.90)', border: '1px solid rgba(34,197,94,0.55)', borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: 7, fontWeight: 900, color: '#22c55e', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Start</span>
                  {first && <span style={{ fontSize: 7, fontFamily: 'ui-monospace,monospace', color: 'rgba(148,163,184,0.9)', fontWeight: 600 }}>{mkLabel(first.timestamp)}</span>}
                </div>
              </div>
            </div>
            <div className="absolute top-0 left-0 pointer-events-none"
              style={{ transform: `translate3d(${projectedReplayEndpoints.end.x}px, ${projectedReplayEndpoints.end.y}px, 0) translate(-50%, -50%)`, zIndex: 25 }}>
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <div style={{ width: 13, height: 13, borderRadius: '50%', background: '#ef4444', border: '2.5px solid white', boxShadow: '0 0 10px rgba(239,68,68,0.85)' }}/>
                <div style={{ background: 'rgba(8,12,24,0.90)', border: '1px solid rgba(239,68,68,0.55)', borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: 7, fontWeight: 900, color: '#ef4444', letterSpacing: '0.1em', textTransform: 'uppercase' }}>End</span>
                  {last && <span style={{ fontSize: 7, fontFamily: 'ui-monospace,monospace', color: 'rgba(148,163,184,0.9)', fontWeight: 600 }}>{mkLabel(last.timestamp)}</span>}
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {projectedReplayPos && historyReplayData.length > 0 && (
        <div
          className="absolute top-0 left-0 pointer-events-none"
          style={{
            transform: `translate3d(${projectedReplayPos.x}px, ${projectedReplayPos.y}px, 0) translate(-50%, -50%)`,
            willChange: 'transform',
            zIndex: 30,
            transition: isReplaying ? `transform ${Math.max(100, Math.round(700 / Math.max(replaySpeed, 1)))}ms linear` : 'none',
          }}
        >
          <div style={{ position: 'absolute', inset: '-6px', borderRadius: '50%', border: '1.5px solid rgba(245,158,11,0.5)', animation: 'ping 1.5s cubic-bezier(0,0,0.2,1) infinite' }} />
          <svg width="26" height="26" viewBox="0 0 24 24" fill="#f59e0b"
            style={{ filter: 'drop-shadow(0 0 6px rgba(245,158,11,0.95)) drop-shadow(0 0 2px rgba(0,0,0,0.9))', transform: `rotate(${projectedReplayPos.heading}deg)` }}>
            <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-4.5l8 2.5z"/>
          </svg>
          {(() => {
            const ev = historyReplayData[replayIndex];
            if (!ev) return null;
            const t = new Date(ev.timestamp);
            const label = isNaN(t.getTime()) ? '' : t.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
            return label ? (
              <div style={{ position: 'absolute', top: '28px', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap',
                background: 'rgba(8,12,24,0.88)', backdropFilter: 'blur(4px)', border: '1px solid rgba(245,158,11,0.4)',
                borderRadius: '4px', padding: '1px 5px', fontSize: '8px', fontWeight: 700, color: '#f59e0b', fontFamily: 'ui-monospace,monospace' }}>
                {label}
                {historyReplayData[replayIndex]?.speed > 0 && ` · ${Math.round(historyReplayData[replayIndex].speed)}kt`}
              </div>
            ) : null;
          })()}
        </div>
      )}

      {airstripLayerModel.mode === 'cluster' && airstripLayerModel.clusters.map((cluster) => (
        <div
          key={`cluster-${cluster.key}`}
          className="absolute top-0 left-0 pointer-events-none"
          style={{ transform: `translate3d(${cluster.x}px, ${cluster.y}px, 0) translate(-50%, -50%)` }}
        >
          <div
            className={`${dark ? 'bg-slate-900/85 border-white/20 text-white' : 'bg-white/90 border-slate-300 text-slate-700'} rounded-full border backdrop-blur-sm shadow-lg`}
            style={{ minWidth: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px', fontSize: 10, fontWeight: 800 }}
          >
            {cluster.count}
          </div>
        </div>
      ))}

      {airstripLayerModel.mode === 'marker' && airstripLayerModel.markers.map((a) => {
        const zoom = airstripLayerModel.zoom;
        const detailTier = zoom > 12 ? 'full' : zoom >= 9 ? 'code' : 'minimal';
        const showRunway = detailTier === 'full' && !!a.runway && !/^\d+$/.test(a.runway.trim());
        const showCard = detailTier !== 'minimal';
        const isHovered = hoveredAirstrip === a.code;
        const iconPx = detailTier === 'full' ? 14 : 11;
        return (
          <div
            key={a.code}
            className="absolute top-0 left-0"
            style={{ transform: `translate3d(${a.x}px, ${a.y}px, 0) translateX(-50%) translateY(-${iconPx / 2}px)`, pointerEvents: 'auto', cursor: 'pointer', zIndex: isHovered ? 50 : 1 }}
            onMouseEnter={() => setHoveredAirstrip(a.code)}
            onMouseLeave={() => setHoveredAirstrip(null)}
          >
            {/* Flex column: icon centered on coordinate, cards stack below in normal flow */}
            <div className="flex flex-col items-center" style={{ gap: '3px', position: 'relative' }}>
              <svg width={iconPx} height={iconPx} viewBox="0 0 24 24" fill="none" style={{ filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.9))' }}>
                <rect x="10" y="2" width="4" height="20" rx="1" fill="white" fillOpacity="0.95"/>
                <rect x="11.5" y="5" width="1" height="3" rx="0.5" fill="rgba(0,0,0,0.6)"/>
                <rect x="11.5" y="10" width="1" height="3" rx="0.5" fill="rgba(0,0,0,0.6)"/>
                <rect x="11.5" y="15" width="1" height="3" rx="0.5" fill="rgba(0,0,0,0.6)"/>
                <line x1="5" y1="4" x2="10" y2="4" stroke="white" strokeWidth="1.5" strokeOpacity="0.7"/>
                <line x1="14" y1="4" x2="19" y2="4" stroke="white" strokeWidth="1.5" strokeOpacity="0.7"/>
                <line x1="5" y1="20" x2="10" y2="20" stroke="white" strokeWidth="1.5" strokeOpacity="0.7"/>
                <line x1="14" y1="20" x2="19" y2="20" stroke="white" strokeWidth="1.5" strokeOpacity="0.7"/>
              </svg>

              {/* Name/elevation card + weather — in-flow below the icon */}
              {showCard && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', pointerEvents: 'none' }}>
                  <div style={{
                    background: dark
                      ? (isHovered ? 'rgba(15,20,40,0.94)' : 'rgba(10,14,26,0.78)')
                      : (isHovered ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.88)'),
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    border: isHovered
                      ? '1px solid rgba(245,158,11,0.6)'
                      : dark ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(148,163,184,0.4)',
                    borderRadius: '7px',
                    padding: detailTier === 'code' ? '2px 5px' : '3px 7px 4px',
                    boxShadow: dark
                      ? '0 2px 8px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.07)'
                      : '0 4px 12px rgba(15,23,42,0.14)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '1px',
                    minWidth: detailTier === 'full' ? '82px' : '30px',
                    transition: 'all 0.15s ease',
                  }}>
                    <span style={{
                      fontSize: detailTier === 'code' ? '8px' : '9px',
                      fontWeight: 900,
                      color: isHovered ? '#f59e0b' : dark ? 'rgba(255,255,255,0.95)' : 'rgba(15,23,42,0.92)',
                      fontFamily: 'ui-monospace,monospace',
                      letterSpacing: '0.1em',
                      whiteSpace: 'nowrap',
                      lineHeight: 1,
                    }}>{a.icao || a.code}</span>
                    {detailTier === 'full' && (
                      <span style={{
                        fontSize: '7px',
                        color: dark ? 'rgba(148,163,184,0.9)' : 'rgba(71,85,105,0.95)',
                        whiteSpace: 'nowrap',
                        lineHeight: 1,
                        fontWeight: 600,
                        maxWidth: '130px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {a.name}
                        {showRunway ? ` · ${a.runway}` : ''}
                      </span>
                    )}
                    {detailTier === 'full' && (
                      <span style={{
                        fontSize: '7px',
                        color: dark ? 'rgba(203,213,225,0.95)' : 'rgba(30,41,59,0.8)',
                        whiteSpace: 'nowrap',
                        lineHeight: 1,
                        fontWeight: 700,
                        fontFamily: 'ui-monospace,monospace',
                      }}>
                        {a.elevationFt ? `${Math.round(a.elevationFt)}ft` : '--'}
                        {showRunway ? ` · RWY ${a.runway}` : ''}
                      </span>
                    )}
                  </div>
                  {detailTier === 'full' && a.weather && (
                    <div style={{
                      borderRadius: 6,
                      padding: '3px 6px',
                      minWidth: 78,
                      background: dark ? 'rgba(2,6,23,0.82)' : 'rgba(255,255,255,0.9)',
                      border: dark ? '1px solid rgba(148,163,184,0.35)' : '1px solid rgba(148,163,184,0.45)',
                      boxShadow: dark ? '0 4px 14px rgba(2,6,23,0.55)' : '0 4px 12px rgba(15,23,42,0.12)',
                      backdropFilter: 'blur(8px)',
                    }}>
                      <div style={{ fontSize: 7, fontWeight: 800, letterSpacing: '0.06em', color: dark ? '#93c5fd' : '#1e40af' }}>{a.icao || a.code}</div>
                      <div style={{ fontSize: 9, fontWeight: 800, color: dark ? '#f8fafc' : '#0f172a' }}>
                        {typeof a.weather.temperatureC === 'number' ? `${Math.round(a.weather.temperatureC)}°C` : '--'}
                      </div>
                      <div style={{ fontSize: 7, color: dark ? 'rgba(203,213,225,0.92)' : 'rgba(51,65,85,0.95)' }}>
                        {typeof a.weather.windDirectionDeg === 'number' && typeof a.weather.windSpeedKt === 'number'
                          ? `${String(a.weather.windDirectionDeg).padStart(3, '0')}/${String(Math.round(a.weather.windSpeedKt)).padStart(2, '0')}kt`
                          : '--'}
                      </div>
                      <div style={{ fontSize: 7, color: dark ? 'rgba(148,163,184,0.95)' : 'rgba(100,116,139,0.95)' }}>
                        {a.weather.condition || '--'}
                      </div>
                      {a.windAdvisory.condition !== 'UNKNOWN' && a.windAdvisory.bestRunway && (
                        <div style={{ marginTop: 1, fontSize: 7, fontWeight: 800, color: dark ? '#facc15' : '#92400e' }}>
                          RWY {a.windAdvisory.bestRunway} · {a.windAdvisory.condition}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Hover detail tooltip — above the flex column (position:relative on parent) */}
              {isHovered && (
                <div style={{
                  position: 'absolute', bottom: '100%', marginBottom: '8px', left: '50%', transform: 'translateX(-50%)',
                  background: 'rgba(8,12,24,0.96)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(245,158,11,0.35)', borderRadius: '10px', padding: '10px 13px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.8)', minWidth: '170px', zIndex: 999, pointerEvents: 'none',
                }}>
                  <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid rgba(245,158,11,0.35)' }} />
                  <div style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ fontSize: '10px', fontWeight: 900, color: '#f59e0b', fontFamily: 'ui-monospace,monospace', letterSpacing: '0.08em' }}>{a.icao || a.code}</span>
                    <span style={{ fontSize: '9px', color: 'rgba(148,163,184,0.8)', marginLeft: '6px', fontWeight: 600 }}>{a.country || ''}</span>
                    <div style={{ fontSize: '8.5px', color: 'rgba(226,232,240,0.9)', fontWeight: 600, marginTop: '2px', maxWidth: '155px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {a.runway && !/^\d+$/.test(a.runway.trim()) && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{ fontSize: '8px', color: 'rgba(100,116,139,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Runway</span>
                        <span style={{ fontSize: '8px', color: 'rgba(226,232,240,0.9)', fontWeight: 700, fontFamily: 'ui-monospace,monospace' }}>
                          {a.runway}
                          {a.lighted && <span style={{ marginLeft: 4, color: '#fbbf24' }} title="Lighted">✦</span>}
                        </span>
                      </div>
                    )}
                    {((a.runwayLengthFt != null && a.runwayLengthFt > 0) || (a.runwayLengthM != null && a.runwayLengthM > 0)) && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{ fontSize: '8px', color: 'rgba(100,116,139,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Length</span>
                        <span style={{ fontSize: '8px', color: 'rgba(226,232,240,0.9)', fontWeight: 700, fontFamily: 'ui-monospace,monospace' }}>
                          {a.runwayLengthFt != null && a.runwayLengthFt > 0
                            ? `${Math.round(a.runwayLengthFt).toLocaleString()}ft · ${Math.round(a.runwayLengthFt * 0.3048)}m`
                            : `${Math.round(a.runwayLengthM!)}m · ${Math.round(a.runwayLengthM! * 3.281).toLocaleString()}ft`
                          }
                          {a.runwayWidthFt != null && a.runwayWidthFt > 0 && (
                            <span style={{ color: 'rgba(148,163,184,0.7)', marginLeft: 3 }}>× {a.runwayWidthFt}ft</span>
                          )}
                        </span>
                      </div>
                    )}
                    {(a.surfaceLabel || a.surfaceType) && a.surfaceType !== 'U' && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{ fontSize: '8px', color: 'rgba(100,116,139,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Surface</span>
                        <span style={{ fontSize: '8px', color: 'rgba(226,232,240,0.9)', fontWeight: 700 }}>{a.surfaceLabel || a.surfaceType}</span>
                      </div>
                    )}
                    {a.elevationFt != null && a.elevationFt > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{ fontSize: '8px', color: 'rgba(100,116,139,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Elevation</span>
                        <span style={{ fontSize: '8px', color: 'rgba(226,232,240,0.9)', fontWeight: 700, fontFamily: 'ui-monospace,monospace' }}>{Math.round(a.elevationFt).toLocaleString()} ft</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                      <span style={{ fontSize: '8px', color: 'rgba(100,116,139,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Fuel</span>
                      <span style={{ fontSize: '8px', fontWeight: 700, color: a.fuelAvailable ? '#34d399' : '#f87171' }}>{a.fuelAvailable ? 'Available' : 'None'}</span>
                    </div>
                    {(a.landingFee != null && a.landingFee > 0) && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{ fontSize: '8px', color: 'rgba(100,116,139,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Landing</span>
                        <span style={{ fontSize: '8px', color: 'rgba(226,232,240,0.9)', fontWeight: 700, fontFamily: 'ui-monospace,monospace' }}>${a.landingFee}</span>
                      </div>
                    )}
                    {(a.handlingFee != null && a.handlingFee > 0) && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{ fontSize: '8px', color: 'rgba(100,116,139,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Handling</span>
                        <span style={{ fontSize: '8px', color: 'rgba(226,232,240,0.9)', fontWeight: 700, fontFamily: 'ui-monospace,monospace' }}>${a.handlingFee}</span>
                      </div>
                    )}
                    {a.weather && (
                      <div style={{ marginTop: 3, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        <div style={{ fontSize: '8px', color: '#93c5fd', fontWeight: 700, marginBottom: 2 }}>Weather ({a.weather.sourceStationCode})</div>
                        <div style={{ fontSize: '8px', color: 'rgba(226,232,240,0.9)', fontFamily: 'ui-monospace,monospace' }}>
                          {typeof a.weather.temperatureC === 'number' ? `${Math.round(a.weather.temperatureC)}°C` : '--'} · {typeof a.weather.windDirectionDeg === 'number' && typeof a.weather.windSpeedKt === 'number' ? `${String(a.weather.windDirectionDeg).padStart(3, '0')}/${Math.round(a.weather.windSpeedKt)}kt` : '--'} · {a.weather.condition || '--'}
                        </div>
                      </div>
                    )}
                    {a.windAdvisory.condition !== 'UNKNOWN' && (
                      <div style={{ marginTop: 3, paddingTop: 6, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        <div style={{ fontSize: '8px', color: '#fbbf24', fontWeight: 700, marginBottom: 2 }}>Operational Wind</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                          <span style={{ fontSize: '8px', color: 'rgba(100,116,139,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Best RWY</span>
                          <span style={{ fontSize: '8px', color: 'rgba(226,232,240,0.9)', fontWeight: 700, fontFamily: 'ui-monospace,monospace' }}>{a.windAdvisory.bestRunway || '--'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                          <span style={{ fontSize: '8px', color: 'rgba(100,116,139,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Headwind</span>
                          <span style={{ fontSize: '8px', color: 'rgba(226,232,240,0.9)', fontWeight: 700, fontFamily: 'ui-monospace,monospace' }}>
                            {typeof a.windAdvisory.headwindComponentKt === 'number' ? `${Math.round(a.windAdvisory.headwindComponentKt)}kt` : '--'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                          <span style={{ fontSize: '8px', color: 'rgba(100,116,139,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Crosswind</span>
                          <span style={{ fontSize: '8px', color: 'rgba(226,232,240,0.9)', fontWeight: 700, fontFamily: 'ui-monospace,monospace' }}>
                            {typeof a.windAdvisory.crosswindComponentKt === 'number' ? `${Math.round(a.windAdvisory.crosswindComponentKt)}kt` : '--'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                          <span style={{ fontSize: '8px', color: 'rgba(100,116,139,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tailwind</span>
                          <span style={{ fontSize: '8px', color: 'rgba(226,232,240,0.9)', fontWeight: 700, fontFamily: 'ui-monospace,monospace' }}>
                            {typeof a.windAdvisory.tailwindComponentKt === 'number' ? `${Math.round(a.windAdvisory.tailwindComponentKt)}kt` : '--'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                          <span style={{ fontSize: '8px', color: 'rgba(100,116,139,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Condition</span>
                          <span style={{ fontSize: '8px', fontWeight: 800, color: a.windAdvisory.condition === 'GOOD' ? '#34d399' : a.windAdvisory.condition === 'CAUTION' ? '#fbbf24' : a.windAdvisory.condition === 'RISK' ? '#f87171' : 'rgba(148,163,184,0.9)' }}>
                            {a.windAdvisory.condition}
                          </span>
                        </div>
                      </div>
                    )}
                    {a.dataSource === 'ourairports' && (
                      <div style={{ marginTop: 2, textAlign: 'right' }}>
                        <span style={{ fontSize: '7px', color: 'rgba(100,116,139,0.6)', fontStyle: 'italic' }}>OurAirports</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>{/* end flex column */}
          </div>
        );
      })}
    </div>
  );
};

export default DashboardMapOverlay;
